// @flow
import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';

import PublicLayout, { layout } from 'gandi.react-public-layout';
import {
  localeSelector,
  withTranslator,
  type TranslatorProps,
} from '@gandi/translate';
import { selectFromConfig } from '@gandi/redux-config';
import { NotificationsPlaceholder } from '@gandi/notifications';
import { selectIsAuthenticatedUser } from '@gandi/authentication';

import type { Location, WithRouterProps } from 'src/helpers/withRouter';
import type { Subject } from '../Support/Contact/types';
import { fetchStatus, selectStatus, selectStatusUrl } from '../modules/status';
import type { Dispatch, FullState } from '../types';

import { selectNavigationItems } from './modules';
import Status from './Status';
import styles from './Layout.scss';

export type LayoutOwnProps = {|
  children: React.Node,
  className?: string | null,
  location?: Location<{|
    +navigation?: ?string,
    +livechat?: ?string,
    +[string]: ?string,
  |}>,
  mainWrapper?: boolean,
|};

export type LayoutInjected = {|
  ...WithRouterProps<
    {|
      category?: string,
      subject?: Subject,
      topic?: string,
    |},
    {| +navigation?: ?string, +livechat?: ?string, +[string]: ?string |}
  >,
  translator: TranslatorProps,
|};

export type LayoutStateProps = {|
  isAuthenticated: boolean,
  isNavigationExpanded: boolean,
  locale: string,
  navigationItems: Array<{|
    type?: string,
    title: string,
    href?: string,
    to?: string,
  |}>,
  navigationLocales: Array<{|
    name: string,
    code: string,
  |}>,
  status?: 'success' | 'info' | 'warning' | 'danger',
  statusUrl: string,
|};

export type LayoutActionsProps = {|
  fetchStatus: (...$ReadOnlyArray<mixed>) => mixed,
  toggleNavigation: (...$ReadOnlyArray<mixed>) => mixed,
|};

export type Props = {|
  ...LayoutInjected,
  ...LayoutOwnProps,
  ...LayoutStateProps,
  ...LayoutActionsProps,
|};

const locales = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  'zh-hant': '正體中文',
  'zh-hans': '简体中文',
  ja: '日本語',
};

const selectLocales = selectFromConfig<[string]>('locales.available', []);

const mapStateToProps = (
  state: FullState,
  ownProps: { ...LayoutOwnProps, ...LayoutInjected }
): LayoutStateProps => {
  const availableLocales = selectLocales(state);
  const navigationItems = selectNavigationItems(state, ownProps);

  const navigationLocales = availableLocales.map((code: string) => ({
    code,
    name: locales[code] || code.toUpperCase(),
  }));

  return {
    isAuthenticated: selectIsAuthenticatedUser(state),
    isNavigationExpanded: layout.selectors.selectIsNavigationExpanded(state),
    locale: localeSelector(state),
    navigationItems,
    navigationLocales,
    status: selectStatus(state),
    statusUrl: selectStatusUrl(state),
  };
};

const mapActionsToProps = (dispatch: Dispatch): LayoutActionsProps => ({
  fetchStatus: (...args) => dispatch(fetchStatus(...args)),
  toggleNavigation: (...args) =>
    dispatch(layout.actions.toggleNavigation(...args)),
});

class Layout extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    const { location } = props;
    if (
      location &&
      props.isNavigationExpanded !== (location.query.navigation === 'true')
    ) {
      props.toggleNavigation();
    }
  }

  componentDidMount() {
    if (!this.props.status) {
      this.props.fetchStatus();
    }
  }

  onLocaleChange: (evt: SyntheticInputEvent<>) => mixed = evt => {
    evt.preventDefault();
    const locale = evt.target.value;

    if (this.props.locale !== locale) {
      /* eslint-env browser */
      window.location.pathname = window.location.pathname.replace(
        /^\/.*?\//,
        `/${locale}/`
      );
    }
  };

  render(): React.Element<typeof PublicLayout> {
    const headerWidgets = [
      <Status
        dotted={!!this.props.status}
        href={this.props.statusUrl}
        key="status"
        theme={this.props.status}
      >
        {this.props.translator.__('Status')}
      </Status>,
    ];

    const {
      locale,
      isAuthenticated,
      mainWrapper,
      navigationItems,
      navigationLocales,
    } = this.props;

    return (
      <PublicLayout
        currentLocale={locale}
        isAuthenticated={isAuthenticated}
        onLocaleChange={this.onLocaleChange}
        navigationItems={navigationItems}
        navigationLocales={navigationLocales}
        className={clsx(styles.noBannerOnErrorPages, this.props.className)}
        mainWrapper={mainWrapper}
        headerWidgets={headerWidgets}
      >
        <NotificationsPlaceholder />

        {this.props.children}
      </PublicLayout>
    );
  }
}

export default (compose(
  withTranslator({
    propsNamespace: 'translator',
  }),
  // $FlowFixMe[underconstrained-implicit-instantiation]
  connect<
    Props,
    { ...LayoutOwnProps, ...LayoutInjected },
    LayoutStateProps,
    LayoutActionsProps,
    FullState,
    Dispatch
  >(mapStateToProps, mapActionsToProps)
)(Layout): React.ComponentType<LayoutOwnProps>);
